import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { isPast } from "../lib/format";
import { Seo } from "../lib/seo";
import { track } from "../lib/tracking";
import { useLeadDialog } from "../context/LeadDialogContext";
import { useLang } from "../context/LanguageContext";
import { HeroSection } from "../components/course/HeroSection";
import { StickyBar } from "../components/course/StickyBar";
import { Gallery } from "../components/course/Gallery";
import { VideoSection } from "../components/course/VideoSection";
import { Benefits } from "../components/course/Benefits";
import { ProgramAccordion } from "../components/course/ProgramAccordion";
import { SpeakerSection } from "../components/course/SpeakerSection";
import { LocationSection } from "../components/course/LocationSection";
import { LeadSection } from "../components/course/LeadSection";
import { PartialPurchaseSection } from "../components/course/PartialPurchaseSection";
import { EnrollmentSection } from "../components/course/EnrollmentSection";
import { FAQSection } from "../components/course/FAQSection";
import { FinalCTA } from "../components/course/FinalCTA";
import { LeadForm } from "../components/LeadForm";
import { Loader2 } from "lucide-react";

function useScrollDepth() {
  useEffect(() => {
    let fired = false;
    const onScroll = () => {
      if (fired) return;
      const h = document.documentElement;
      const scrolled = (h.scrollTop + window.innerHeight) / h.scrollHeight;
      if (scrolled >= 0.75) {
        fired = true;
        track("scroll_75");
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
}

function TerminatedCourse({ course }) {
  const { t } = useLang();
  return (
    <div data-testid="course-terminated" className="bg-[#F4F8FF]">
      <section className="px-6 md:px-12 max-w-[1100px] mx-auto py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="grayscale-muted overflow-hidden rounded-sm order-2 lg:order-1 bg-[#D5DEEE]">
          {course.speaker_photo && (
            <img src={mediaUrl(course.speaker_photo)} alt={course.speaker_name} className="w-full h-[420px] object-cover object-top" />
          )}
        </div>
        <div className="order-1 lg:order-2">
          <span className="overline text-[#5C6678]">{course.category}</span>
          <h1 className="font-heading text-4xl lg:text-5xl font-light tracking-tight text-[#0B1220] mt-3">
            {course.title}
          </h1>
          <div className="mt-8 bg-[#E9EFFA] border border-[#D5DEEE] rounded-sm p-8">
            <h2 className="font-heading text-2xl text-[#0B1220]">{t("terminated.title")}</h2>
            <p className="mt-3 text-[#5C6678] font-light leading-relaxed">
              {t("terminated.sub")}
            </p>
            <div className="mt-6">
              <LeadForm course={course} source="terminated" simplified />
            </div>
          </div>
          <Link to="/" data-testid="terminated-back" className="inline-block mt-8 text-sm text-[#1452F0] underline underline-offset-4 hover:text-[#0E3DC0]">
            {t("terminated.back")}
          </Link>
        </div>
      </section>
    </div>
  );
}

export default function CoursePage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [course, setCourse] = useState(null);
  const [status, setStatus] = useState("loading");
  const lead = useLeadDialog();
  useScrollDepth();

  useEffect(() => {
    setStatus("loading");
    window.scrollTo(0, 0);
    api
      .get(`/courses/${slug}`, { params: { lang } })
      .then((r) => {
        setCourse(r.data);
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [slug, lang]);

  const seoTitle = course ? (course.meta_title || `${course.title} | Advanced Post Graduate Excellence`) : "Corso | Advanced Post Graduate Excellence";
  const seoDesc = course?.meta_description || course?.subtitle || "";
  const seoCanonical = typeof window !== "undefined" ? window.location.origin + `/corsi/${slug}` : "";

  if (status === "loading") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1452F0]" />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-heading text-5xl font-light text-[#0B1220]">{t("notfound.title")}</h1>
        <p className="mt-3 text-[#5C6678]">{t("notfound.sub")}</p>
        <Link to="/" className="mt-8 bg-[#1452F0] text-[#F4F8FF] rounded-sm px-7 py-3.5 text-sm font-medium">
          {t("notfound.back")}
        </Link>
      </div>
    );
  }

  const ended = isPast(course.date_time);

  if (ended) {
    return <TerminatedCourse course={course} />;
  }

  const onInfo = () => lead?.openLead(course, "landing");
  const onEnroll = () => {
    track("click_iscriviti_online", { course: course.title });
    if (course.enroll_online_url) {
      window.open(course.enroll_online_url, "_blank", "noopener");
    } else {
      document.getElementById("iscrizione")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <Seo title={seoTitle} description={seoDesc} canonical={seoCanonical} image={course.hero_image ? mediaUrl(course.hero_image) : ""} />
      <StickyBar course={course} onInfo={onInfo} onEnroll={onEnroll} />
      <HeroSection course={course} onInfo={onInfo} onEnroll={onEnroll} />
      <Gallery images={course.gallery} />
      <VideoSection videos={course.videos} poster={course.gallery?.[0]} />
      <Benefits items={course.why_participate} />
      <ProgramAccordion program={course.program} />
      <SpeakerSection course={course} />
      <LocationSection course={course} />
      <LeadSection course={course} />
      <PartialPurchaseSection course={course} />
      <EnrollmentSection course={course} onEnroll={onEnroll} />
      <FAQSection faq={course.faq} />
      <FinalCTA course={course} onInfo={onInfo} onEnroll={onEnroll} />
    </>
  );
}
